const TOKEN_KEY = 'access_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/**
 * Build a user-facing error message from an API error response body.
 * @param {object} data
 * @param {number} [status]
 */
export function formatApiError(data, status) {
  if (typeof data?.message === 'string' && data.message.trim() !== '') {
    return data.message.trim();
  }

  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    const parts = [];
    for (const [field, messages] of Object.entries(errors)) {
      const list = Array.isArray(messages) ? messages : [messages];
      for (const msg of list) {
        if (typeof msg === 'string' && msg.trim() !== '') {
          parts.push(field === 'message' || field === 'error' ? msg.trim() : `${field}: ${msg.trim()}`);
        }
      }
    }
    if (parts.length > 0) {
      return parts.slice(0, 3).join(' ');
    }
  }

  if (typeof data?.error === 'string' && data.error.trim() !== '') {
    return data.error.trim();
  }

  return defaultStatusMessage(status);
}

/**
 * @param {number} [status]
 */
function defaultStatusMessage(status) {
  switch (status) {
    case 400:
      return 'The request was invalid. Check your input and try again.';
    case 401:
      return 'You must sign in to perform this action.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 408:
    case 504:
      return 'The server took too long to respond. Please try again.';
    case 409:
      return 'This action conflicts with the current state. Refresh the page and try again.';
    case 413:
      return 'The request was too large for the server. Large images are compressed automatically — try Save again.';
    case 422:
      return 'The submitted data is invalid. Please check the form and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'A server error occurred. Please try again later.';
    case 502:
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return status ? `Request failed (HTTP ${status}).` : 'Request failed.';
  }
}

/**
 * @param {string} path
 * @param {{ method?: string, body?: object, skipAuth?: boolean }} [options]
 */
let refreshInFlight = null;

async function refreshAccessToken() {
  const current = getToken();
  if (!current) return null;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const res = await fetch('/api/refresh', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${current}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.access_token) return null;
      setToken(data.access_token);
      return data.access_token;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function apiJson(path, options = {}) {
  const { method = 'GET', body, skipAuth = false, retried = false } = options;
  const headers = { Accept: 'application/json' };
  if (!skipAuth) {
    const t = getToken();
    if (t) {
      headers.Authorization = `Bearer ${t}`;
    }
  }
  const isFormData = body instanceof FormData;
  if (body && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(path, {
    method,
    headers,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
  });
  if (res.status === 401 && !skipAuth && !retried) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      return apiJson(path, { ...options, retried: true });
    }
    setToken(null);
    window.dispatchEvent(new CustomEvent('portal:session-expired'));
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(formatApiError(data, res.status));
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

/**
 * @param {number} blockId
 * @param {{ clientId?: number; projectId?: number }} [opts]
 */
export async function fetchAuthedBlob(path) {
  const headers = { Accept: '*/*' };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(path, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatApiError(data, res.status));
  }
  return res.blob();
}

export async function fetchFaqBlockFile(blockId, opts = {}) {
  let url = opts.fileUrl || `/api/my/faq/blocks/${blockId}/file`;
  if (!opts.fileUrl && opts.projectId != null) {
    url = `/api/my/projects/${opts.projectId}/faq/blocks/${blockId}/file`;
  } else if (!opts.fileUrl && opts.clientId != null) {
    url = `/api/admin/clients/${opts.clientId}/faq/blocks/${blockId}/file`;
  }

  const headers = { Accept: '*/*' };
  const t = getToken();
  if (t) {
    headers.Authorization = `Bearer ${t}`;
  }

  const res = await fetch(url, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatApiError(data, res.status));
  }

  return res.blob();
}

/**
 * @param {number} conversationId
 * @param {File} file
 * @param {string} [caption]
 * @param {{ guestToken?: string, organizationId?: number }} [opts]
 */
export async function uploadChatAttachment(conversationId, file, caption, opts = {}) {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);

  let url = `/api/chat/conversations/${conversationId}/attachments`;
  if (opts.guestToken && opts.organizationId) {
    url = `/api/public/chat/${opts.organizationId}/conversations/${conversationId}/attachments`;
    form.append('guest_token', opts.guestToken);
  }

  const headers = { Accept: 'application/json' };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  const res = await fetch(url, { method: 'POST', headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(formatApiError(data, res.status));
  }
  return data;
}

/**
 * @param {number} messageId
 * @param {{ guestToken?: string, organizationId?: number }} [opts]
 */
export async function fetchChatAttachmentBlob(messageId, opts = {}) {
  let url = `/api/chat/messages/${messageId}/attachment`;
  if (opts.guestToken && opts.organizationId) {
    url = `/api/public/chat/${opts.organizationId}/messages/${messageId}/attachment?guest_token=${encodeURIComponent(opts.guestToken)}`;
  }

  const headers = { Accept: '*/*' };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  const res = await fetch(url, { headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatApiError(data, res.status));
  }
  return res.blob();
}
